import React, { useState, useMemo, useEffect } from 'react';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Button from '../../components/ui/Button';
import UserFilters from './components/UserFilters';
import UserTable from './components/UserTable';
import UserModal from './components/UserModal';
import UserDetailsModal from './components/UserDetailsModal';
import { getUsers, createUser, updateUser, deleteUser } from '../../services/userService';

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ column: 'fullName', direction: 'asc' });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users?.filter(user => {
      const matchesSearch = 
        user?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      const matchesRole = roleFilter === 'all' || user?.role === roleFilter;

      return matchesSearch && matchesRole;
    });

    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.column];
      let bValue = b?.[sortConfig?.column];

      if (sortConfig?.column === 'createdAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [users, searchTerm, roleFilter, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev?.column === column && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setIsDetailsModalOpen(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsAddModalOpen(true);
  };

  const handleDelete = async (user) => {
    if (window.confirm(`Are you sure you want to delete ${user?.fullName}? This action cannot be undone.`)) {
      try {
        await deleteUser(user?.id);
        await loadUsers();
      } catch (err) {
        console.error('Error deleting user:', err);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const handleSubmit = async (userData, userId) => {
    try {
      if (userId) {
        await updateUser(userId, userData);
      } else {
        await createUser(userData);
      }
      await loadUsers();
      setIsAddModalOpen(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Error saving user:', err);
      alert('Failed to save user. Please try again.');
    }
  };

  const quickActions = [
    {
      label: 'Add User',
      onClick: () => {
        setSelectedUser(null);
        setIsAddModalOpen(true);
      },
      variant: 'default',
      icon: 'UserPlus'
    },
    {
      label: 'Refresh',
      onClick: loadUsers,
      variant: 'outline',
      icon: 'RefreshCw'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <QuickActionToolbar actions={quickActions} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage team member accounts, roles, and permissions across accounting modules
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-error/10 border border-error rounded-lg p-4 flex items-center gap-3">
            <span className="text-sm text-error">{error}</span>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6">
          <UserFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            roleFilter={roleFilter}
            onRoleFilterChange={setRoleFilter}
            onReset={handleResetFilters}
          />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-lg shadow-sm border border-border p-4">
            <p className="text-sm text-muted-foreground mb-1">Total Users</p>
            <p className="text-2xl font-bold text-foreground">{users?.length}</p>
          </div>
          <div className="bg-card rounded-lg shadow-sm border border-border p-4">
            <p className="text-sm text-muted-foreground mb-1">Administrators</p>
            <p className="text-2xl font-bold text-purple-600">{users?.filter(u => u?.role === 'admin')?.length}</p>
          </div>
          <div className="bg-card rounded-lg shadow-sm border border-border p-4">
            <p className="text-sm text-muted-foreground mb-1">Accountants</p>
            <p className="text-2xl font-bold text-blue-600">{users?.filter(u => u?.role === 'accountant')?.length}</p>
          </div>
          <div className="bg-card rounded-lg shadow-sm border border-border p-4">
            <p className="text-sm text-muted-foreground mb-1">Other Roles</p>
            <p className="text-2xl font-bold text-green-600">{users?.filter(u => u?.role === 'manager' || u?.role === 'user')?.length}</p>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading users...</p>
          </div>
        ) : filteredAndSortedUsers?.length === 0 ? (
          <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
            <p className="text-muted-foreground">No users found. Add your first team member to get started.</p>
            <Button
              className="mt-4"
              iconName="UserPlus"
              onClick={() => {
                setSelectedUser(null);
                setIsAddModalOpen(true);
              }}
            >
              Add User
            </Button>
          </div>
        ) : (
          <UserTable
            users={filteredAndSortedUsers}
            sortConfig={sortConfig}
            onSort={handleSort}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onViewDetails={handleViewDetails}
          />
        )}

        {/* Role Permissions Info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">Role Permissions Overview</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li><strong>Administrator:</strong> Full system access, manage users, configure settings, access all modules</li>
            <li><strong>Accountant:</strong> Create/edit transactions, generate reports, reconcile accounts, manage customers</li>
            <li><strong>Manager:</strong> View and approve transactions, access reports, monitor team activities</li>
            <li><strong>User:</strong> Basic access to view data, submit expenses, and access assigned modules</li>
          </ul>
        </div>
      </div>

      {/* Modals */}
      <UserModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={handleSubmit}
        user={selectedUser}
      />

      <UserDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />
    </div>
  );
};

export default UserManagement;